import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import {
  Path,
  TreeResource,
  TreeNode,
  Permission,
  PermissionSubject,
  PermissionVerb,
  NodeType,
  PermissionLookup
} from '@s8l/client-tree-lib';

import { CmsService } from 'src/app/services/cms.service';

@Component({
  selector: 'app-sidebar-segment',
  templateUrl: './segment.component.html',
  styleUrls: ['./segment.component.scss']
})
export class SidebarSegmentComponent implements OnChanges {
  @Input() public node: TreeNode;
  @Input() public activePath: Path;
  @Input() public mode = '';
  @Input() public reference: boolean;

  public showChildren = false;
  public isActive = false;
  public canShowChildren = false;
  public isReference = false;
  public canCreate = false;
  public children: TreeNode[];
  public resourceIcon: string;
  public isLoadingChildren = false;
  public childrenLoaded = false;

  constructor(public cms: CmsService, private router: Router) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public ngOnChanges(changes: SimpleChanges): void {
    this.isReference = this.reference || this.node.type === NodeType.Reference;
    this.canCreate = this.cms.hasAccess(new Permission(PermissionSubject.Node, PermissionVerb.Read | PermissionVerb.Write));

    // Track if children have been loaded/fetched
    this.childrenLoaded = this.node.children && this.node.children.length > 0;

    if (this.node.children) {
      // Ensure all children have initialized children array (avoid null iteration errors)
      this.node.children.forEach(child => {
        if (!child.children) {
          child.children = [];
        }
      });

      const undefinedMeta = this.node.children.filter(child => !child.meta);
      const childWithMeta = this.node.children.find(child => child.meta);

      if (undefinedMeta.length > 0) {
        const meta = childWithMeta
          ? childWithMeta.meta
          : {
              glossary: false,
              notesDisabled: false,
              sidemenu: false
            };
        undefinedMeta.forEach(child => (child.meta = meta));
      }

      try {
        this.children = this.node.children.filter(child => this.cms.config.matchesModifiers(child, []));
      } catch (e) {
        throw new Error(`Error filtering children for node ${this.node.path}: ${e.message}`);
      }
    } else {
      this.children = [];
    }
    this.isActive = this.getIsActive();
    this.canShowChildren = this.getCanShowChildren();
    this.resourceIcon = this.getResourceIcon();

    if (this.isActive) {
      this.showChildren = true;
    }
  }

  private getIsActive() {
    const current = this.activePath.get();
    const path = Path.parse(this.node.path).get();

    for (let i = 0; i < path.length; i++) {
      if (path[i] !== current[i]) {
        return false;
      }
    }
    return true;
  }

  private getCanShowChildren() {
    if (!this.node) {
      return false;
    }
    const rule = this.cms.config.findRule(this.node);
    if (!rule) {
      return false;
    }
    if (this.isReference && this.node.children.length == 0) {
      return false;
    }
    return rule.allowChildren;
  }

  private getResourceIcon() {
    // Defensive: check method exists (might not on incompletely initialized nodes)
    if (typeof this.node.hasResource !== 'function') {
      return undefined;
    }
    if (this.mode === 'quiz' && this.node.hasResource(TreeResource.Quiz)) return 'question-circle';
    if (this.mode === 'wiki' && this.node.hasResource(TreeResource.Wiki)) return 'book';
    if (this.mode === 'glossary' && this.node.hasResource(TreeResource.Glossary)) return 'search-plus';
    if (this.mode === 'impuls' && this.node.hasResource(TreeResource.Impuls)) return 'list';
    if (this.mode === 'casestudy' && this.node.hasResource(TreeResource.Casestudy)) return 'file-edit';
    if (this.mode === 'video' && this.node.hasResource(TreeResource.Video)) return 'film';
    return undefined;
  }

  public async open() {
    // Always fetch fresh children before navigating
    if (this.canShowChildren) {
      await this.loadChildren();
    }
    return this.router.navigate(['/node', this.node.path, this.mode]);
  }

  public create() {
    return this.router.navigate(['/node', this.node.path, 'create']);
  }

  public async toggleChildren() {
    // Always fetch fresh children when expanding
    if (!this.showChildren && this.canShowChildren) {
      await this.loadChildren();
    }
    this.showChildren = !this.showChildren;
  }

  private async loadChildren(): Promise<void> {
    if (this.isLoadingChildren) {
      return;
    }

    try {
      this.isLoadingChildren = true;

      // Fetch children for this specific node using its UUID
      // Include full: true to get permissions data for permission page
      // Use depth: 1 to ensure child nodes have their children available
      const response = await this.cms.cms.treeGet({
        language: this.cms.language,
        parentUuid: this.node.uuid,
        depth: 1
      });

      // Update the node with the fetched children
      if (response.root && response.root.children) {
        // Update global permissions if included in response
        // This is needed for the permission page to display role-level permissions
        if (response.permissions) {
          this.cms.permissions = PermissionLookup.of(response.permissions);
        }

        // Merge strategy: update only the grandchildren, keep existing immediate children
        const rawFetchedChildren = response.root.children || [];

        // Convert plain objects to actual TreeNode instances (like updateTree does)
        const fetchedChildren = rawFetchedChildren.map(rawChild => this.createTreeNode(rawChild));

        // Deserialize and process fetched children
        fetchedChildren.forEach(fetchedChild => {
          if (fetchedChild.permissions && typeof fetchedChild.permissions === 'object' && !('grants' in fetchedChild.permissions)) {
            fetchedChild.permissions = PermissionLookup.of(fetchedChild.permissions);
          }
          if (!fetchedChild.children) {
            fetchedChild.children = [];
          }

          // Find matching child in existing children by uuid
          const existingChild = this.node.children?.find(c => c.uuid === fetchedChild.uuid);
          if (existingChild) {
            // Update only the grandchildren (don't replace the child object itself)
            // Also convert grandchildren to TreeNode instances
            existingChild.children = (fetchedChild.children || []).map(grandchild => this.createTreeNode(grandchild));
          } else {
            // Child didn't exist, add it
            if (!this.node.children) {
              this.node.children = [];
            }
            this.node.children.push(fetchedChild);
          }
        });

        this.childrenLoaded = true;

        // Re-process children like in ngOnChanges
        const undefinedMeta = this.node.children.filter(child => !child.meta);
        const childWithMeta = this.node.children.find(child => child.meta);

        if (undefinedMeta.length > 0) {
          const meta = childWithMeta
            ? childWithMeta.meta
            : {
                glossary: false,
                notesDisabled: false,
                sidemenu: false
              };
          undefinedMeta.forEach(child => (child.meta = meta));
        }

        try {
          this.children = this.node.children.filter(child => this.cms.config.matchesModifiers(child, []));
          this.cms.notifyTreeUpdated();
        } catch (e) {
          throw new Error(`Error filtering children for node ${this.node.path}: ${e.message}`);
        }
      }
    } catch (error) {
      console.error(`Failed to load children for node ${this.node.path}:`, error);
      // Don't mark as loaded if there was an error
      this.childrenLoaded = false;
    } finally {
      this.isLoadingChildren = false;
    }
  }

  private createTreeNode(data: any): TreeNode {
    // Convert plain objects to actual TreeNode instances with all methods
    return new TreeNode({
      ...data,
      children: (data.children || []).map((child: any) => this.createTreeNode(child))
    });
  }

  public trackByUUID(index: number, node: TreeNode) {
    return node.uuid;
  }
}
